import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './TenantUsers.css';

const formatHasForwarding = (value) => {
  if (value === true || value === 1 || String(value).toLowerCase() === 'true') {
    return 'Yes';
  }
  if (value === false || value === 0 || String(value).toLowerCase() === 'false') {
    return 'No';
  }
  return '—';
};

const formatForwardingRules = (user) => {
  const addresses = Array.isArray(user.forwardingAddresses) ? user.forwardingAddresses : [];
  if (addresses.length > 0) {
    return addresses.join(', ');
  }

  if (user.HasForwardingRules) {
    return 'Configured (no destination addresses listed)';
  }

  if (user.ForwardingRules == null || user.ForwardingRules === '') {
    return '—';
  }

  if (typeof user.ForwardingRules === 'string') {
    return user.ForwardingRules;
  }

  try {
    return JSON.stringify(user.ForwardingRules);
  } catch {
    return '—';
  }
};

const formatLastLogon = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const SORT_COLUMNS = [
  { key: 'displayName', label: 'Display Name' },
  { key: 'userPrincipalName', label: 'User Principal Name' },
  { key: 'lastLogon', label: 'Last logon' },
  { key: 'HasForwardingRules', label: 'Mail Forwarding' },
  { key: 'forwardingRules', label: 'Forwarding Rules' }
];

const getSortValue = (user, key) => {
  if (key === 'lastLogon') {
    return user.lastLogon ? new Date(user.lastLogon).getTime() : 0;
  }
  if (key === 'HasForwardingRules') {
    return user.HasForwardingRules ? 1 : 0;
  }
  if (key === 'forwardingRules') {
    return formatForwardingRules(user).toLowerCase();
  }
  return String(user[key] || '').toLowerCase();
};

const TenantUsers = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDisabledOnly, setShowDisabledOnly] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'displayName', direction: 'asc' });

  const fetchUsers = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch users');
        setUsers([]);
      } else {
        setUsers(Array.isArray(data.users) ? data.users : []);
      }
    } catch (err) {
      console.error('Error fetching tenant users:', err);
      setError('Network error. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchUsers();
    } else {
      setUsers([]);
      setLoading(false);
    }
  }, [selectedTenant, fetchUsers]);

  const filteredUsers = useMemo(() => {
    const lower = searchTerm.trim().toLowerCase();
    return users.filter((user) => {
      if (showDisabledOnly && !user.accountDisabled) {
        return false;
      }
      if (!lower) return true;
      const forwardingText = formatForwardingRules(user).toLowerCase();
      const lastLogonText = formatLastLogon(user.lastLogon).toLowerCase();
      const statusText = user.accountDisabled ? 'disabled' : 'enabled';
      return (
        (user.displayName && user.displayName.toLowerCase().includes(lower)) ||
        (user.userPrincipalName && user.userPrincipalName.toLowerCase().includes(lower)) ||
        forwardingText.includes(lower) ||
        lastLogonText.includes(lower) ||
        statusText.includes(lower) ||
        formatHasForwarding(user.HasForwardingRules).toLowerCase().includes(lower)
      );
    });
  }, [users, searchTerm, showDisabledOnly]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'lastLogon' ? 'desc' : 'asc' };
    });
  };

  const displayedUsers = useMemo(() => {
    const rows = [...filteredUsers];
    const factor = sortConfig.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);
      if (aVal < bVal) return -1 * factor;
      if (aVal > bVal) return 1 * factor;
      const aName = String(a.displayName || '').toLowerCase();
      const bName = String(b.displayName || '').toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
    return rows;
  }, [filteredUsers, sortConfig]);

  const handleExport = () => {
    const tenantName = selectedTenant?.name || 'users';
    const headers = [
      'Display Name',
      'User Principal Name',
      'Last logon',
      'Account status',
      'Mail Forwarding',
      'Forwarding Rules'
    ];
    const lines = [
      headers.join(','),
      ...displayedUsers.map((user) => {
        const lastLogon = formatLastLogon(user.lastLogon);
        return [
          csvEscape(user.displayName || ''),
          csvEscape(user.userPrincipalName || ''),
          csvEscape(lastLogon === '—' ? '' : lastLogon),
          user.accountDisabled ? 'Disabled' : 'Enabled',
          csvEscape(formatHasForwarding(user.HasForwardingRules)),
          csvEscape(formatForwardingRules(user) === '—' ? '' : formatForwardingRules(user))
        ].join(',');
      })
    ];
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `${tenantName}-users-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!selectedTenant) {
    return (
      <div className="tenant-users-container">
        <div className="tenant-users-header">
          <h2>Tenant Users</h2>
        </div>
        <div className="tenant-users-empty">
          <Icon name="user" size="large" className="tenant-users-empty-icon" />
          <p>Please select a tenant to view users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-users-container">
      <div className="tenant-users-header">
        <div>
          <h2>Tenant Users - {selectedTenant.name}</h2>
          <p>Directory users with mail forwarding status and configured rules.</p>
        </div>
        <div className="tenant-users-actions">
          <div className="tenant-users-search-group">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tenant-users-search"
            />
            <label className="tenant-users-filter">
              <input
                type="checkbox"
                checked={showDisabledOnly}
                onChange={(e) => setShowDisabledOnly(e.target.checked)}
              />
              <span>Disabled accounts</span>
            </label>
          </div>
          <button
            type="button"
            className="tenant-users-export"
            onClick={handleExport}
            disabled={loading || displayedUsers.length === 0}
          >
            <Icon name="list" size="small" />
            Export
          </button>
        </div>
      </div>

      {!loading && !error && (
        <div className="tenant-users-count">
          Showing {displayedUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
          {showDisabledOnly ? ' (disabled only)' : ''}
        </div>
      )}

      {loading ? (
        <div className="tenant-users-loading">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="tenant-users-error">⚠️ {error}</div>
      ) : displayedUsers.length === 0 ? (
        <div className="tenant-users-empty">
          <Icon name="user" size="large" className="tenant-users-empty-icon" />
          <h3>No users found</h3>
          <p>Try adjusting the search or filters.</p>
        </div>
      ) : (
        <div className="tenant-users-table-wrapper">
          <table className="tenant-users-table">
            <thead>
              <tr>
                {SORT_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className="tenant-users-sortable"
                    onClick={() => handleSort(column.key)}
                    aria-sort={
                      sortConfig.key === column.key
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <span className="tenant-users-header-content">
                      {column.label}
                      {sortConfig.key === column.key && (
                        <span className="tenant-users-sort-indicator">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map((user, index) => (
                <tr
                  key={`${user.userPrincipalName || 'user'}-${index}`}
                  className={user.accountDisabled ? 'tenant-users-row-disabled' : undefined}
                >
                  <td>
                    <span className="tenant-users-name-cell">
                      {user.displayName || '—'}
                      {user.accountDisabled && (
                        <span className="tenant-users-disabled-badge">Disabled</span>
                      )}
                    </span>
                  </td>
                  <td>{user.userPrincipalName || '—'}</td>
                  <td>{formatLastLogon(user.lastLogon)}</td>
                  <td>
                    <span
                      className={`tenant-users-forwarding-flag ${
                        user.HasForwardingRules ? 'enabled' : 'disabled'
                      }`}
                    >
                      {formatHasForwarding(user.HasForwardingRules)}
                    </span>
                  </td>
                  <td className="tenant-users-forwarding-rules">
                    {formatForwardingRules(user)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TenantUsers;
